import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { getCurrencySymbol, getCurrencyCode, getSymbol } from '../utils/currency';
import {
  ArrowLeft, Plus, Pencil, Trash, Check, X, Clock,
  Users, Lightning, ToggleLeft, ToggleRight, ListBullets
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesManager = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [form, setForm] = useState({ name: '', description: '', price: '', duration_minutes: '', capacity: '', available: true });

  const fetchData = useCallback(async () => {
    try {
      const [listingRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/listings/${listingId}`),
        axios.get(`${API_URL}/api/services/${listingId}`)
      ]);
      setListing(listingRes.data);
      setServices(servicesRes.data.services || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [listingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const resetForm = () => {
    setForm({ name: '', description: '', price: '', duration_minutes: '', capacity: '', available: true });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (svc) => {
    setForm({
      name: svc.name,
      description: svc.description || '',
      price: svc.price,
      duration_minutes: svc.duration_minutes || '',
      capacity: svc.capacity || '',
      available: svc.available !== false
    });
    setEditingId(svc.service_id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.price) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: parseFloat(form.price),
        duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
        available: form.available
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/services/${listingId}/${editingId}`, payload);
        showSuccess('Service updated');
      } else {
        await axios.post(`${API_URL}/api/services/${listingId}`, payload);
        showSuccess('Service added');
      }
      resetForm();
      await fetchData();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/api/services/${listingId}/${serviceId}`);
      showSuccess('Service deleted');
      await fetchData();
    } catch (e) { console.error(e); }
  };

  const handleToggle = async (serviceId) => {
    try {
      await axios.patch(`${API_URL}/api/services/${listingId}/${serviceId}/toggle`, {});
      await fetchData();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="services-manager">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-full" data-testid="back-btn">
            <ArrowLeft size={22} className="text-slate-700" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">Services Manager</h1>
            <p className="text-xs text-slate-500 truncate">{listing?.title}</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-9 px-4 text-sm font-semibold"
            data-testid="add-service-btn">
            <Plus size={16} className="mr-1" weight="bold" /> Add Service
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 text-sm font-medium flex items-center gap-2" data-testid="success-msg">
            <Check size={18} weight="bold" /> {successMsg}
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <Card className="border-blue-200 bg-blue-50/30" data-testid="service-form">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-slate-900">
                {editingId ? 'Edit Service' : 'Add New Service'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Service Name *</label>
                <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Haircut, X-Ray, Standard Room" className="mt-1 h-11 rounded-xl" data-testid="service-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Description</label>
                <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description of the service" className="mt-1 h-11 rounded-xl" data-testid="service-desc-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Price ({getCurrencyCode(listing?.country)}) *</label>
                  <Input type="number" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                    placeholder="500" className="mt-1 h-11 rounded-xl" data-testid="service-price-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Duration (min)</label>
                  <Input type="number" value={form.duration_minutes} onChange={(e) => setForm(f => ({ ...f, duration_minutes: e.target.value }))}
                    placeholder="30" className="mt-1 h-11 rounded-xl" data-testid="service-duration-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Capacity</label>
                  <Input type="number" value={form.capacity} onChange={(e) => setForm(f => ({ ...f, capacity: e.target.value }))}
                    placeholder="10" className="mt-1 h-11 rounded-xl" data-testid="service-capacity-input" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <button onClick={() => setForm(f => ({ ...f, available: !f.available }))} data-testid="service-available-toggle">
                    {form.available ?
                      <ToggleRight size={32} className="text-emerald-500" weight="fill" /> :
                      <ToggleLeft size={32} className="text-slate-400" weight="fill" />}
                  </button>
                  <span className="text-sm text-slate-600">{form.available ? 'Available' : 'Unavailable'}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={resetForm} className="rounded-xl h-9" data-testid="cancel-service-btn">Cancel</Button>
                  <Button onClick={handleSave} disabled={saving || !form.name.trim() || !form.price}
                    className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-9 px-5" data-testid="save-service-btn">
                    {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services List */}
        {services.length > 0 ? (
          <div className="space-y-2">
            {services.map(svc => (
              <div key={svc.service_id}
                className={`bg-white rounded-xl border p-4 flex items-center gap-3 transition-opacity ${!svc.available ? 'opacity-50 border-slate-200' : 'border-slate-100'}`}
                data-testid={`service-${svc.service_id}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${svc.available ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                  <ListBullets size={20} className={svc.available ? 'text-emerald-600' : 'text-slate-400'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{svc.name}</p>
                  {svc.description && <p className="text-xs text-slate-500 line-clamp-1">{svc.description}</p>}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1 font-semibold text-slate-700">
                      {getSymbol(listing?.currency || listing?.country)}{svc.price}
                    </span>
                    {svc.duration_minutes && (
                      <span className="flex items-center gap-1"><Clock size={12} /> {svc.duration_minutes} min</span>
                    )}
                    {svc.capacity && (
                      <span className="flex items-center gap-1"><Users size={12} /> Cap: {svc.capacity}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleToggle(svc.service_id)}
                    className="p-2 hover:bg-slate-100 rounded-lg" title="Toggle availability"
                    data-testid={`toggle-${svc.service_id}`}>
                    {svc.available ?
                      <ToggleRight size={22} className="text-emerald-500" weight="fill" /> :
                      <ToggleLeft size={22} className="text-slate-400" weight="fill" />}
                  </button>
                  <button onClick={() => handleEdit(svc)}
                    className="p-2 hover:bg-blue-50 rounded-lg" title="Edit"
                    data-testid={`edit-${svc.service_id}`}>
                    <Pencil size={16} className="text-blue-500" />
                  </button>
                  <button onClick={() => handleDelete(svc.service_id)}
                    className="p-2 hover:bg-red-50 rounded-lg" title="Delete"
                    data-testid={`delete-${svc.service_id}`}>
                    <Trash size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : !showForm && (
          <div className="text-center py-16">
            <ListBullets size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-1">No services added yet</p>
            <p className="text-sm text-slate-400 mb-4">Add services like "Haircut", "Standard Room", "X-Ray" etc.</p>
            <Button onClick={() => setShowForm(true)} className="bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="add-first-service-btn">
              <Plus size={16} className="mr-1" /> Add First Service
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesManager;
